import { useEffect, useState } from 'react';
/**
 * Vanilla Inbox Stream Manager
 * Usable in Service Workers (Extension) and React Components (App/Website).
 */
export class InboxStream {
    client;
    abortController = null;
    retryTimeout = null;
    isConnected = false;
    onMessage = () => { };
    onSnapshot = () => { };
    onStatusChange = () => { };
    onRevoked = () => { };
    constructor(client) {
        this.client = client;
    }
    get connected() {
        return this.isConnected;
    }
    disconnect() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }
        this.isConnected = false;
        this.onStatusChange('disconnected');
    }
    async connect() {
        // Prevent concurrent connections if already connected or trying to connect
        if (this.isConnected || (this.abortController && !this.abortController.signal.aborted)) {
            return;
        }
        this.disconnect(); // Ensure any previous connection/timeout is cleared
        this.onStatusChange('connecting');
        // Reset timeout flag
        let isTimeout = false;
        const controller = new AbortController();
        this.abortController = controller;
        // Watchdog: Server sends keep-alive every 30s. If silence > 45s, kill it.
        let lastDataTime = Date.now();
        const watchdog = setInterval(() => {
            if (Date.now() - lastDataTime > 45000) {
                console.warn('InboxStream: Connection timed out');
                isTimeout = true;
                controller.abort();
            }
        }, 5000);
        try {
            const response = await this.client.devices.connectStream(controller.signal);
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Fatal error, don't retry
                    this.disconnect();
                    return;
                }
                throw new Error(`Stream connection failed: ${response.status} `);
            }
            // Only set connected if the controller hasn't been aborted externally
            if (this.abortController === controller && !controller.signal.aborted) {
                this.isConnected = true;
                this.onStatusChange('connected');
            }
            else {
                // Connection was aborted while fetching, clean up and return
                this.disconnect();
                return;
            }
            const reader = response.body?.getReader();
            if (!reader)
                throw new Error('No readable stream body');
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                // Check if the controller has been aborted before reading
                if (controller.signal.aborted) {
                    break;
                }
                const { done, value } = await reader.read();
                if (done)
                    break;
                lastDataTime = Date.now();
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() ?? '';
                for (const block of lines) {
                    this.processSSEBlock(block);
                }
            }
        }
        catch (err) {
            clearInterval(watchdog); // Stop watchdog
            if (err.name === 'AbortError' && !isTimeout) {
                // This is expected when disconnect is called manually
                return;
            }
            console.error('Inbox Stream Error:', err);
            // Retry if manual disconnect didn't happen OR if it was a timeout
            if (this.abortController === controller) {
                this.isConnected = false;
                this.onStatusChange('connecting'); // Retrying
                this.retryTimeout = setTimeout(() => this.connect(), 3000);
            }
            else {
                this.disconnect(); // Ensure clean state if aborted
            }
        }
    }
    processSSEBlock(block) {
        const lines = block.split('\n');
        let eventType = 'message';
        let dataStr = '';
        for (const line of lines) {
            if (line.startsWith('event: ')) {
                eventType = line.substring(7).trim();
            }
            else if (line.startsWith('data: ')) {
                dataStr += line.substring(6);
            }
        }
        if (!dataStr)
            return;
        try {
            const payload = JSON.parse(dataStr);
            if (eventType === 'snapshot') {
                if (payload.messages && Array.isArray(payload.messages)) {
                    this.onSnapshot(payload.messages);
                }
            }
            else if (eventType === 'signal') {
                if (payload.message) {
                    this.onMessage(payload.message);
                }
            }
            else if (eventType === 'revoked') {
                this.onRevoked();
                this.disconnect();
            }
        }
        catch (e) {
            console.error('Failed to parse SSE data:', e);
        }
    }
}
/**
 * React Hook for Inbox Stream
 */
export function useInboxStream(client, initialMessages = []) {
    const [messages, setMessages] = useState(initialMessages);
    const [status, setStatus] = useState('disconnected');
    // Initial load sync
    useEffect(() => {
        if (initialMessages.length > 0) {
            setMessages(initialMessages);
        }
    }, [initialMessages]);
    useEffect(() => {
        const stream = new InboxStream(client);
        stream.onStatusChange = setStatus;
        stream.onSnapshot = (msgs) => {
            setMessages(msgs);
        };
        stream.onMessage = (msg) => {
            setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id))
                    return prev;
                return [msg, ...prev];
            });
        };
        stream.connect();
        return () => {
            stream.disconnect();
        };
    }, [client]); // Re-connect if client instance changes
    return { messages, status };
}
//# sourceMappingURL=index.js.map